import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import * as AssessmentService from '@services/AssessmentService';
import { useAppTheme } from '@contexts/ThemeContext';
import { Plus, Trash2, Check, BookOpen, AlertCircle } from 'lucide-react';
import Spinner from './components/Spinner';
import Create360Question from './Create360Question';
import { isFeedbackType, isSubjectiveFeedbackType } from '@config/assessmentTypes';
import './CreateQuestion.css';

interface Module {
  moduleId: number;
  title: string;
}

interface QuestionForm {
  questionText: string;
  options: string[];
  correctAnswerIndex: number | null;
}

interface QuestionOptionWithId extends AssessmentService.QuestionOption {
  optionId?: number;
}

interface SavedQuestion extends AssessmentService.Question {
  options: QuestionOptionWithId[];
}

const CreateQuestion: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get('assessmentId');

  const [modules, setModules] = useState<Module[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedQuestions, setSavedQuestions] = useState<SavedQuestion[]>([]);
  const [assessmentTitle, setAssessmentTitle] = useState<string>('');
  const [assessmentTypeId, setAssessmentTypeId] = useState<number | null>(null);
  // For 270 feedback: assessment-wide count of subjective (text) questions per entity, for the 3/entity cap.
  const [subjectiveCounts, setSubjectiveCounts] = useState<Record<'participant' | 'reportee' | 'manager', number>>({
    participant: 0,
    reportee: 0,
    manager: 0,
  });

  const [questionForm, setQuestionForm] = useState<QuestionForm>({
    questionText: '',
    options: ['', '', '', ''],
    correctAnswerIndex: null
  });

  useEffect(() => {
    if (assessmentId) {
      fetchAssessmentDetails();
      fetchModules();
    } else {
      setError('No assessment ID provided');
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    if (selectedModuleId) {
      fetchModuleQuestions();
    }
  }, [selectedModuleId]);

  // For 270 (subjective-capable) feedback, keep the assessment-wide per-entity subjective counts in
  // sync so Create360Question can show (n/3) and enforce the 3-per-entity cap across all modules.
  useEffect(() => {
    if (isSubjectiveFeedbackType(assessmentTypeId) && modules.length > 0) {
      refreshSubjectiveCounts(modules);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentTypeId, modules]);

  const fetchAssessmentDetails = async () => {
    if (!assessmentId) return;

    try {
      const response = await AssessmentService.getAssessmentById(parseInt(assessmentId));

      if (response.success && response.data) {
        setAssessmentTitle(response.data.assessmentTitle);
        setAssessmentTypeId(response.data.assessment_type_id || null);
      }
    } catch (err: any) {
      console.error('Error fetching assessment details:', err);
    }
  };

  const fetchModules = async () => {
    if (!assessmentId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await AssessmentService.getAssessmentModules(parseInt(assessmentId));

      if (response.success && response.data) {
        // Sort modules by moduleId in ascending order
        const sortedModules = [...response.data].sort((a, b) => a.moduleId - b.moduleId);
        setModules(sortedModules);

        // Auto-select first module if available
        if (sortedModules.length > 0 && !selectedModuleId) {
          setSelectedModuleId(sortedModules[0].moduleId);
        }
      } else {
        setError(response.error?.message || 'Failed to fetch modules');
      }
    } catch (err: any) {
      console.error('Error fetching modules:', err);
      setError(err.message || 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleQuestions = async () => {
    if (!selectedModuleId) return;

    try {
      const response = await AssessmentService.getModuleQuestions(selectedModuleId);

      if (response.success && response.data) {
        // getModuleQuestions already returns questions with their options
        setSavedQuestions(response.data as SavedQuestion[]);
      }
    } catch (err: any) {
      console.error('Error fetching questions:', err);
    }
  };

  // Tally subjective (type='text') questions per entity across every module of the assessment.
  // Subjective questions are not tied to a module, so the 3/entity cap is enforced assessment-wide.
  const refreshSubjectiveCounts = async (moduleList: Module[]) => {
    try {
      const results = await Promise.all(
        moduleList.map(m => AssessmentService.getModuleQuestions(m.moduleId))
      );
      const counts = { participant: 0, reportee: 0, manager: 0 };
      results.forEach(res => {
        if (res.success && Array.isArray(res.data)) {
          (res.data as any[]).forEach(q => {
            if (q.type === 'text' && (q.applicable_to in counts)) {
              counts[q.applicable_to as keyof typeof counts] += 1;
            }
          });
        }
      });
      setSubjectiveCounts(counts);
    } catch (err) {
      console.error('Error counting subjective questions:', err);
    }
  };

  // After a question is added: refresh the current module's list and, for 270, the per-entity counts.
  const handleQuestionAdded = () => {
    fetchModuleQuestions();
    if (isSubjectiveFeedbackType(assessmentTypeId) && modules.length > 0) {
      refreshSubjectiveCounts(modules);
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = value;
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  const handleSetCorrectAnswer = (index: number) => {
    setQuestionForm({ ...questionForm, correctAnswerIndex: index });
  };

  const resetForm = () => {
    setQuestionForm({
      questionText: '',
      options: ['', '', '', ''],
      correctAnswerIndex: null
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedModuleId) {
      setError('Please select a module');
      return;
    }

    if (!questionForm.questionText.trim()) {
      setError('Please enter a question');
      return;
    }

    if (questionForm.options.some(opt => !opt.trim())) {
      setError('Please fill all 4 options');
      return;
    }

    if (questionForm.correctAnswerIndex === null) {
      setError('Please mark one option as correct');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const questionData: AssessmentService.CreateQuestionData = {
        moduleId: selectedModuleId,
        text: questionForm.questionText,
        options: questionForm.options.map((text, index) => ({
          text,
          isCorrect: index === questionForm.correctAnswerIndex
        }))
      };

      const response = await AssessmentService.createQuestion(questionData);

      if (response.success) {
        setSuccessMessage('Question added successfully!');
        resetForm();
        // Refresh the questions list
        fetchModuleQuestions();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.error?.message || 'Failed to create question');
      }
    } catch (err: any) {
      console.error('Error creating question:', err);
      setError(err.message || 'Failed to create question');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="create-question-page" data-theme={isDarkMode ? 'dark' : 'light'}>
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (!assessmentId) {
    return (
      <Layout>
        <div className="create-question-page" data-theme={isDarkMode ? 'dark' : 'light'}>
          <div className="error-container">
            <AlertCircle size={48} />
            <h2>No Assessment Selected</h2>
            <p>Please select an assessment to create questions.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (modules.length === 0) {
    return (
      <Layout>
        <div className="create-question-page" data-theme={isDarkMode ? 'dark' : 'light'}>
          <div className="error-container">
            <AlertCircle size={48} />
            <h2>No Modules Found</h2>
            <p>Please add modules to this assessment before creating questions.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const selectedModule = modules.find(m => m.moduleId === selectedModuleId);

  return (
    <Layout>
      <div className="create-question-page" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="page-header">
          <div>
            <h1 className="page-title">Create Questions</h1>
            <p className="page-subtitle">Add questions for: {assessmentTitle || 'Loading...'}</p>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success">
            <Check size={20} />
            <span>{successMessage}</span>
          </div>
        )}

        <div className="main-grid">
          {/* Module Selection Sidebar */}
          <div className="sidebar">
            <div className="card">
              <h2 className="section-title">
                <BookOpen size={20} />
                Select Module
              </h2>
              <div className="module-list">
                {modules.map(module => (
                  <button
                    key={module.moduleId}
                    onClick={() => setSelectedModuleId(module.moduleId)}
                    className={`module-card ${selectedModuleId === module.moduleId ? 'active' : ''}`}
                  >
                    <div className="module-name">{module.title}</div>
                    <div className="module-count">
                      {savedQuestions.filter(q => q.moduleId === module.moduleId).length} questions
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Question Form */}
          <div className="main-content">
            {selectedModuleId ? (
              <>
                {/* Feedback (360/270) authoring, else regular question form */}
                {isFeedbackType(assessmentTypeId) ? (
                  <Create360Question
                    selectedModuleId={selectedModuleId}
                    moduleName={selectedModule?.title || ''}
                    onQuestionAdded={handleQuestionAdded}
                    isDarkMode={isDarkMode}
                    allowSubjective={isSubjectiveFeedbackType(assessmentTypeId)}
                    subjectiveCounts={subjectiveCounts}
                  />
                ) : (
                  <div className="card">
                    <h2 className="section-title">Add New Question</h2>
                    <p className="section-description">
                      Creating question for: <strong>{selectedModule?.title}</strong>
                    </p>

                    <form onSubmit={handleSubmit} className="question-form">
                      <div className="form-group">
                        <label className="form-label">Question Text</label>
                        <textarea
                          value={questionForm.questionText}
                          onChange={(e) => setQuestionForm({ ...questionForm, questionText: e.target.value })}
                          className="form-textarea"
                          rows={3}
                          placeholder="Enter your question here..."
                          required
                        />
                      </div>

                      <div className="options-section">
                        <label className="form-label">Options (Select the correct answer)</label>
                        <div className="options-grid">
                          {questionForm.options.map((option, index) => (
                            <div key={index} className="option-group">
                              <div className="option-header">
                                <span className="option-label">Option {index + 1}</span>
                              </div>
                              <div className="option-input-wrapper">
                                <input
                                  type="text"
                                  value={option}
                                  onChange={(e) => handleOptionChange(index, e.target.value)}
                                  className="form-input"
                                  placeholder={`Enter option ${index + 1}`}
                                  required
                                />
                                <button
                                  type="button"
                                  onClick={() => handleSetCorrectAnswer(index)}
                                  className={`check-button ${questionForm.correctAnswerIndex === index ? 'active' : ''}`}
                                  title="Mark as correct answer"
                                >
                                  <Check size={20} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="btn-submit"
                        disabled={submitting}
                      >
                        <Plus size={20} />
                        <span>{submitting ? 'Adding Question...' : 'Add Question'}</span>
                      </button>
                    </form>
                  </div>
                )}

                {/* Saved Questions List */}
                <div className="card">
                  <h2 className="section-title">
                    Saved Questions ({savedQuestions.length})
                  </h2>

                  {savedQuestions.length === 0 ? (
                    <div className="empty-state">
                      <p>No questions added yet. Create your first question above!</p>
                    </div>
                  ) : (
                    <div className="questions-list">
                      {savedQuestions.map((question, idx) => (
                        <div key={question.questionId} className="question-card">
                          <div className="question-header">
                            <h3 className="question-text">
                              {idx + 1}. {question.text}
                            </h3>
                            {(question as any).type === 'text' && (
                              <span className="option-value" style={{ marginLeft: 8 }}>Open-ended</span>
                            )}
                          </div>
                          {question.options && question.options.length > 0 && (
                            <div className="saved-options-list">
                              {question.options.map((option, optIdx) => (
                                <div
                                  key={optIdx}
                                  className={`saved-option ${assessmentTypeId !== 3 && option.isCorrect ? 'correct-option' : ''}`}
                                >
                                  {/* Show checkmark only for non-feedback questions */}
                                  {!isFeedbackType(assessmentTypeId) && option.isCorrect && (
                                    <Check size={16} className="check-icon" />
                                  )}
                                  <span className="option-text">{option.text}</span>
                                  {/* Show Likert value for feedback questions */}
                                  {isFeedbackType(assessmentTypeId) && option.value !== undefined && (
                                    <span className="option-value">({option.value})</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="card empty-state-card">
                <BookOpen size={64} className="empty-icon" />
                <h3 className="empty-title">Select a Module to Begin</h3>
                <p className="empty-text">
                  Choose a module from the left to start adding questions
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateQuestion;
